import { useCallback, useMemo } from 'react';

import { IBaseItem, ISanityItem } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { EntryTypeEnum } from 'generated/graphql-gateway';
import { useUpsellItemsByIdsQuery } from 'generated/sanity-graphql';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useMenuContext } from 'state/menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

import { UpsellEntry, UpsellResolutionState } from './types';
import { itemIdExistsInEntries, legacyItemExistsInEntries } from './utils';

const MAX_UPSELL_ITEMS = 5;

interface UsePersonalizedMenuFeatureUpsellOptions {
  personalizedSanityIds?: string[];
}

export const usePersonalizedMenuFeatureUpsell = ({
  personalizedSanityIds,
}: UsePersonalizedMenuFeatureUpsellOptions) => {
  const { filterForAvailability } = useMenuContext();
  const { getOneMenuItemAvailability } = useMenuContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { cartEntries } = useCart();
  const {
    hasError: hasUnifiedMenuError,
    indexedMenu,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();

  const uniquePersonalizedSanityIds = useMemo(() => [...new Set(personalizedSanityIds ?? [])], [
    personalizedSanityIds,
  ]);

  const hasPersonalizedSource = uniquePersonalizedSanityIds.length > 0;

  // Because this personalized upsell items are not part of the feature menu,
  // but instead they come from the experiment as ids, we need to fetch them in order to check legacy item availability.
  const {
    data: personalizedLegacyData,
    error: personalizedLegacyError,
    loading: personalizedLegacyLoading,
  } = useUpsellItemsByIdsQuery({
    variables: { ids: uniquePersonalizedSanityIds },
    skip: !hasPersonalizedSource || isUnifiedMenuEnabled,
  });

  const isLegacyItemAvailable = useCallback(
    (item: IBaseItem) => !!filterForAvailability(item as ISanityItem),
    [filterForAvailability]
  );

  const isOneMenuItemAvailable = useCallback(
    (item: ILocalizedMenuEntry) =>
      getOneMenuItemAvailability(item.id)?.availabilityStatus === ItemAvailabilityStatus.AVAILABLE,
    [getOneMenuItemAvailability]
  );

  const treatmentResult = useMemo((): {
    items: UpsellEntry[];
    loading: boolean;
    resolutionState: UpsellResolutionState;
  } => {
    if (!hasPersonalizedSource) {
      return { items: [], loading: false, resolutionState: 'hidden' };
    }

    if (isUnifiedMenuEnabled) {
      if (isUnifiedMenuLoading || (!indexedMenu.isLoaded() && !hasUnifiedMenuError)) {
        return { items: [], loading: true, resolutionState: 'loading' };
      }

      if (hasUnifiedMenuError) {
        return { items: [], loading: false, resolutionState: 'hidden' };
      }

      const items: UpsellEntry[] = uniquePersonalizedSanityIds
        .map(sanityId => indexedMenu.find(sanityId, { type: EntryTypeEnum.ITEM }))
        .filter((item): item is NonNullable<typeof item> => !!item)
        .filter(item => !itemIdExistsInEntries(item.id, cartEntries))
        .filter(isOneMenuItemAvailable)
        .slice(0, MAX_UPSELL_ITEMS)
        .map(item => ({ source: 'one-menu' as const, item }));

      return {
        items,
        loading: false,
        resolutionState: items.length ? 'treatment' : 'hidden',
      };
    }

    if (personalizedLegacyLoading) {
      return { items: [], loading: true, resolutionState: 'loading' };
    }

    if (personalizedLegacyError) {
      return { items: [], loading: false, resolutionState: 'hidden' };
    }

    const itemsById = new Map(
      (personalizedLegacyData?.allItems ?? []).map(item => [item._id, item])
    );

    const items: UpsellEntry[] = uniquePersonalizedSanityIds
      .map(sanityId => itemsById.get(sanityId))
      .filter((item): item is NonNullable<typeof item> => !!item)
      .map(item => (item as unknown) as IBaseItem)
      .filter(item => !legacyItemExistsInEntries(item, cartEntries))
      .filter(isLegacyItemAvailable)
      .slice(0, MAX_UPSELL_ITEMS)
      .map(item => ({ source: 'legacy' as const, item }));

    return {
      items,
      loading: false,
      resolutionState: items.length ? 'treatment' : 'hidden',
    };
  }, [
    cartEntries,
    hasPersonalizedSource,
    hasUnifiedMenuError,
    indexedMenu,
    isLegacyItemAvailable,
    isOneMenuItemAvailable,
    isUnifiedMenuEnabled,
    isUnifiedMenuLoading,
    personalizedLegacyData?.allItems,
    personalizedLegacyError,
    personalizedLegacyLoading,
    uniquePersonalizedSanityIds,
  ]);

  return treatmentResult;
};
